import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import ProductModalContainer from "./ProductModalContainer";
import type { TransformedProduct } from "../../types/productApiTypes";
import { getProductImageSource } from "../../helpers/productImgHelpers";

interface ProductCardProps {
  product: TransformedProduct;
  viewMode?: "grid" | "list";
}

interface ProductCardSkeletonProps {
  viewMode?: "grid" | "list";
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  viewMode = "grid",
}) => {
  const { t } = useTranslation("common");
  const [imageLoaded, setImageLoaded] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const imageSource = getProductImageSource(product);

  const handleImageLoad = (): void => {
    setImageLoaded(true);
  };

  const handleQuickView = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsModalOpen(true);
  };

  return (
    <>
      <div
        className={`product-card product-card-${viewMode}`}
        data-product-id={product.id}
      >
        <a href={product.permalink} className="product-link">
          <div
            className={`product-image ${
              imageLoaded ? "image-loaded" : "image-loading"
            }`}
          >
            <div className="image-placeholder"></div>
            <img
              src={imageSource}
              alt={product.name}
              onLoad={handleImageLoad}
              loading="lazy"
              className={imageLoaded ? "visible" : ""}
            />
            <div className="quick-view-wrapper">
              <button
                className="quick-view-btn"
                onClick={handleQuickView}
                title={t("product.quickView")}
                aria-label={t("product.quickView")}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="24"
                  height="24"
                >
                  <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                </svg>
              </button>
            </div>
          </div>
          <div className="product-content">
            <h2 className="product-title">{product.name}</h2>
            {viewMode === "list" && product.shortDescription && (
              <div
                className="product-short-description"
                dangerouslySetInnerHTML={{ __html: product.shortDescription }}
              />
            )}
            <div className="product-card-actions">
              <span className="button go-to-product-btn">
                {t("product.goToProduct")}
              </span>
            </div>
          </div>
        </a>
      </div>
      <ProductModalContainer
        productId={product.id}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
};

const ProductCardSkeleton: React.FC<ProductCardSkeletonProps> = ({
  viewMode = "grid",
}) => {
  return (
    <div
      className={`product-card product-card-skeleton product-card-${viewMode}`}
    >
      <div className="product-link">
        <div className="product-image">
          <Skeleton height="100%" width="100%" />
        </div>
        <div className="product-content">
          <h2 className="product-title">
            <Skeleton height={24} width="80%" />
          </h2>
          {viewMode === "list" && (
            <div className="product-short-description">
              <Skeleton count={2} />
            </div>
          )}
          <div className="product-card-actions">
            <Skeleton height={40} width={120} />
          </div>
        </div>
      </div>
    </div>
  );
};

type ProductCardComponent = React.FC<ProductCardProps> & {
  Skeleton: React.FC<ProductCardSkeletonProps>;
};

(ProductCard as ProductCardComponent).Skeleton = ProductCardSkeleton;

export default ProductCard as ProductCardComponent;
